import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getPageMeta, SITE_NAME } from '../../seo/pageMeta';

// 운영 도메인 — 빌드 시 REACT_APP_SITE_URL로 override 가능 (Vercel/Netlify 환경변수 등)
const SITE_URL = (process.env.REACT_APP_SITE_URL || 'https://vipvantour.com').replace(/\/$/, '');

// 홈/공항 중복 해소: /transfer/airport 는 / 와 동일 콘텐츠 → SEO상 / 로 통합(canonical/메타 일원화).
const CANONICAL_ALIAS = { '/transfer/airport': '/' };

const BE_TO_URL_PREFIX = { ko: '', en: '/en', ja: '/ja', zh: '/zh', es: '/es' };
const HREFLANG = { ko: 'ko-KR', en: 'en', ja: 'ja-JP', zh: 'zh-CN', es: 'es' };
const OG_LOCALE = { ko: 'ko_KR', en: 'en_US', ja: 'ja_JP', zh: 'zh_CN', es: 'es_ES' };

/**
 * 페이지 SEO 태그 (title/description/canonical/hreflang/OG/Twitter/JSON-LD).
 *
 * @param {string} [route] - SEO route key (PAGE_META의 키). 미지정 시 현재 pathname 사용.
 * @param {object} [jsonLd] - schema.org JSON-LD 객체. 직접 주입 시 serviceType 자동 빌드 무시.
 * @param {string} [serviceType] - schema.org Service.serviceType 값. 지정 시 jsonLd 미주입이면 현재 페이지 메타로
 *                                  Service JSON-LD 자동 빌드. 예) "Airport Transfer", "Private Tour"
 * @param {string} [image] - OG/Twitter 이미지 절대 URL (기본: 사이트 기본 이미지)
 * @param {string} [titleOverride] - 동적 페이지(투어 상세 등)에서 제목 직접 지정
 * @param {string} [descriptionOverride] - 동적 페이지에서 설명 직접 지정
 */
export default function SEO({ route, jsonLd, serviceType, image, titleOverride, descriptionOverride, renderH1 = false, noindex = false }) {
  const { i18n } = useTranslation();
  const beLang = i18n.language || 'ko';
  const { pathname } = useLocation();

  // route 미지정 시 현재 경로 사용 (basename은 useLocation이 이미 제거한 상태)
  const resolvedRoute = route || pathname || '/';
  // canonical alias 적용 → 메타·canonical·hreflang·og:url 모두 통합 대상 경로로 일원화.
  const seoRoute = CANONICAL_ALIAS[resolvedRoute] || resolvedRoute;
  const meta = getPageMeta(seoRoute, beLang);

  const title = titleOverride || meta.title || SITE_NAME;
  // 시트 확정 H1(meta.h1)이 있으면 그대로 사용(타이틀과 별개 카피). 없으면 타이틀에서 사이트명 접미사 제거.
  const h1Text = meta.h1 || title.replace(/\s*[|\-–]\s*VIP Van Tour\s*$/i, '').trim() || SITE_NAME;
  const description = descriptionOverride || meta.description || '';
  // 시트 규칙: OG Title = H1, OG Description = Meta Description.
  const ogTitle = meta.h1 || h1Text;
  // OG 기본 폴백(페이지별 ogImage 없을 때) = 브랜드 대표 사진(밴+서울). 로고보다 공유 썸네일에 적합.
  const ogImage = image || (meta.ogImage ? `${SITE_URL}${meta.ogImage}` : `${SITE_URL}/images/logo/vip-van-tour-premium-van.webp`);
  const ogImageAlt = meta.ogImageAlt || title;

  // canonical URL: SITE_URL + 언어 prefix + 경로(통합 경로 기준)
  const langPrefix = BE_TO_URL_PREFIX[beLang] || '';
  const pathPart = seoRoute === '/' ? '' : seoRoute;
  const canonical = `${SITE_URL}${langPrefix}${pathPart}` || `${SITE_URL}/`;

  // Service JSON-LD 자동 빌드 — jsonLd 직접 주입이 우선.
  const finalJsonLd = jsonLd || (serviceType
    ? buildServiceJsonLd({ name: title, description, serviceType, image: ogImage })
    : null);

  return (
    <>
    <Helmet>
      <html lang={HREFLANG[beLang] || beLang} />
      <title>{title}</title>
      <meta name="description" content={description} />
      {meta.keywords && <meta name="keywords" content={meta.keywords.join(', ')} />}
      {/* noindex 페이지(404 등): Helmet 경유로 head 에 정확히 주입 */}
      {noindex && <meta name="robots" content="noindex,nofollow" />}
      <link rel="canonical" href={canonical} />

      {/* hreflang: 5개 언어별 대체 URL */}
      {Object.entries(BE_TO_URL_PREFIX).map(([lng, prefix]) => (
        <link
          key={lng}
          rel="alternate"
          hrefLang={HREFLANG[lng]}
          href={`${SITE_URL}${prefix}${pathPart}` || `${SITE_URL}/`}
        />
      ))}
      <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}${pathPart}` || `${SITE_URL}/`} />

      {/* Open Graph (시트: og:title=H1, og:description=Meta Desc, og:image=페이지별) */}
      <meta property="og:type" content="website" />
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:title" content={ogTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:alt" content={ogImageAlt} />
      <meta property="og:locale" content={OG_LOCALE[beLang] || 'en_US'} />

      {/* Twitter */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={ogTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={ogImageAlt} />

      {/* JSON-LD 구조화 데이터 */}
      {finalJsonLd && (
        <script type="application/ld+json">
          {JSON.stringify(finalJsonLd)}
        </script>
      )}
    </Helmet>
    {renderH1 && <h1 className="sr-only">{h1Text}</h1>}
    </>
  );
}

/** 공용 organization JSON-LD (Footer 등 사이트 전체에서 한 번만 노출 권장).
 *  TravelAgency 는 LocalBusiness 하위 타입 → 로컬/AI 검색의 사업자(NAP) 신호로도 사용됨.
 *  ⚠ 정확한 도로명 주소·좌표·영업시간은 사업자 정보 확정 후 보강(허위 기재 금지). */
export function getOrganizationJsonLd(opts = {}) {
  const ar = opts.aggregateRating;
  const hasRating = ar && Number(ar.ratingValue) > 0 && Number(ar.reviewCount) > 0;
  return {
    '@context': 'https://schema.org',
    '@type': 'TravelAgency',
    name: SITE_NAME,
    legalName: '주식회사 브이아이피밴투어',
    url: SITE_URL,
    logo: `${SITE_URL}/images/logo/logo.png`,
    // 구글 검색 썸네일 정사각 유도 — 정사각(1:1)/가로형(1.91:1)/로고 다중 비율 제공
    image: [
      `${SITE_URL}/images/og/square/vip-van-tour-premium-van-1200x1200.webp`,
      `${SITE_URL}/images/og/social/vip-van-tour-premium-van.webp`,
      `${SITE_URL}/images/logo/logo.png`,
    ],
    telephone: '+82-10-2250-0365',
    taxID: '145-88-01687',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '백제고분로 116, 312-1호',
      addressLocality: 'Songpa-gu',
      addressRegion: 'Seoul',
      postalCode: '05574',
      addressCountry: 'KR',
    },
    areaServed: { '@type': 'Country', name: 'South Korea' },
    // 영업시간: 평일 09:00~18:00 (대표 확인). 차량 서비스 이용은 24시간이나, 사업장 영업시간은 평일 주간.
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: '+82-10-2250-0365',
      contactType: 'customer service',
      availableLanguage: ['Korean', 'English', 'Japanese', 'Chinese', 'Spanish'],
    },
    sameAs: [
      'https://www.linkedin.com/company/vipvantour/about/',
      'https://www.instagram.com/vipvantour.korea/',
      'https://www.facebook.com/profile.php?id=61573357626146',
    ],
    // 사이트 전체 리뷰 평점 — 실제 집계값이 있을 때만 (허위/빈 별점 금지)
    ...(hasRating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: String(ar.ratingValue),
        reviewCount: String(ar.reviewCount),
      },
    } : {}),
  };
}

/** FAQ 페이지용 FAQPage JSON-LD 빌더. items: [{ question, answer }] */
export function buildFaqJsonLd(items) {
  const list = (items || [])
    .filter((it) => it && it.question && it.answer)
    .map((it) => ({
      '@type': 'Question',
      name: it.question,
      acceptedAnswer: { '@type': 'Answer', text: it.answer },
    }));
  if (list.length === 0) return null;
  return {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: list,
  };
}

/** 투어 상세용 TouristTrip JSON-LD 빌더.
 *  - price 있으면 Offer(가격·재고·URL) 포함
 *  - aggregateRating({ ratingValue, reviewCount }) 은 *실제 데이터가 있을 때만* 전달할 것.
 *    허위 별점 마크업은 구글 수동 조치 대상이므로 값이 없으면 생략(미주입). */
export function buildTourJsonLd({ name, description, image, price, url, aggregateRating }) {
  const hasRating = aggregateRating
    && Number(aggregateRating.ratingValue) > 0
    && Number(aggregateRating.reviewCount) > 0;
  return {
    '@context': 'https://schema.org',
    '@type': 'TouristTrip',
    name,
    ...(description ? { description } : {}),
    ...(image ? { image } : {}),
    provider: { '@type': 'TravelAgency', name: SITE_NAME, url: SITE_URL },
    ...(price ? {
      offers: {
        '@type': 'Offer',
        price: String(price),
        priceCurrency: 'KRW',
        availability: 'https://schema.org/InStock',
        ...(url ? { url } : {}),
      },
    } : {}),
    ...(hasRating ? {
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: String(aggregateRating.ratingValue),
        reviewCount: String(aggregateRating.reviewCount),
      },
    } : {}),
  };
}

/** BreadcrumbList JSON-LD 빌더. items: [{ name, url }] (순서대로) */
export function buildBreadcrumbJsonLd(items) {
  const list = (items || [])
    .filter((it) => it && it.name)
    .map((it, i) => ({
      '@type': 'ListItem',
      position: i + 1,
      name: it.name,
      ...(it.url ? { item: it.url } : {}),
    }));
  if (list.length === 0) return null;
  return { '@context': 'https://schema.org', '@type': 'BreadcrumbList', itemListElement: list };
}

/** 메타 중복 없이 추가 JSON-LD 만 주입하는 경량 컴포넌트 (canonical/OG 등 재출력 방지). */
export function JsonLd({ data }) {
  if (!data) return null;
  return (
    <Helmet>
      <script type="application/ld+json">{JSON.stringify(data)}</script>
    </Helmet>
  );
}

/** 서비스(이동/투어) 페이지용 Service JSON-LD 빌더 */
export function buildServiceJsonLd({ name, description, serviceType, areaServed = 'South Korea', image }) {
  return {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name,
    description,
    serviceType,
    provider: {
      '@type': 'TravelAgency',
      name: SITE_NAME,
      url: SITE_URL,
    },
    areaServed,
    ...(image ? { image } : {}),
  };
}
